import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import employeeAttendanceAPI from '../api/employeeAttendanceAPI';
import employeeAttendanceHrAPI from '../api/employeeAttendanceHrAPI';
import admissionsAPI from '../api/admissionsAPI';
import postgraduateAPI from '../api/postgraduateAPI';
import { parseOfficerFlags, mergeHrAccess, getPostLoginPath } from '../utils/roleAccess';
import examAPI from '../api/examAPI';
import attendanceAPI from '../../attendance/api/attendanceAPI';

const EmployeeAttendanceAuthContext = createContext(null);

const STORAGE_KEY = 'ea_user';
const TOKEN_KEY = 'ea_token';
const HR_TOKEN_KEY = 'ea_hr_token';

const HR_PROBE_OPTS = { suppressUnauthorized: true };

/**
 * Shared officer login: POST /api/employee-attendance/hr/login
 * Same Bearer token for HR (/hr/*) and Admission (/api/admissions/*).
 */
async function resolveOfficerAccess(username, password, existingToken) {
  if (password) {
    try {
      const hrResult = await employeeAttendanceHrAPI.login(
        username,
        password,
        HR_PROBE_OPTS
      );
      if (hrResult?.success === true && hrResult.token) {
        localStorage.setItem(HR_TOKEN_KEY, hrResult.token);
        const flags = parseOfficerFlags(hrResult);
        return {
          ...flags,
          hr_token_ready: true,
          portal_employee_count: hrResult.portal_employee_count ?? 0,
          officer_profile: hrResult,
        };
      }
      return {
        is_hr: false,
        is_admission: false,
        is_exam: false, is_postgraduate: false,
        hr_api_error: hrResult?.message || 'Officer login did not return a token',
      };
    } catch (err) {
      if (err.httpStatus === 404) {
        return { is_hr: false, is_admission: false, is_exam: false, is_postgraduate: false, hr_api_missing: true };
      }
      return { is_hr: false, is_admission: false, is_exam: false, is_postgraduate: false, hr_api_error: err.message };
    }
  }

  if (existingToken) {
    try {
      const me = await employeeAttendanceHrAPI.me(existingToken, HR_PROBE_OPTS);
      if (me?.success) {
        localStorage.setItem(HR_TOKEN_KEY, existingToken);
        const flags = parseOfficerFlags(me);
        return {
          ...flags,
          hr_token_ready: true,
          hr_token_is_staff: true,
          portal_employee_count: me.portal_employee_count ?? 0,
          officer_profile: me,
        };
      }
    } catch (err) {
      // Keep going — admission-only officers may not pass HR /me
      if (err.httpStatus === 404) {
        /* hr/me missing entirely */
      }
    }

    // Do not call /api/admissions/me here — it 404s until backend ships it.
    // Token still present: keep session; caller merges with stored is_admission / is_hr.
    localStorage.setItem(HR_TOKEN_KEY, existingToken);
    return {
      is_hr: false,
      is_admission: false,
      is_exam: false, is_postgraduate: false,
      hr_token_ready: true,
      officer_restore_soft: true,
    };
  }

  return { is_hr: false, is_admission: false, is_exam: false, is_postgraduate: false, portal_employee_count: 0 };
}

export function EmployeeAttendanceAuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const clearError = useCallback(() => setError(null), []);
  const navigate = useNavigate();

  const clearSession = useCallback(() => {
    setUser(null);
    localStorage.removeItem(STORAGE_KEY);
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(HR_TOKEN_KEY);
    localStorage.removeItem('attendance_user');
    localStorage.removeItem('attendance_token');
  }, []);

  const handleUnauthorized = useCallback(() => {
    clearSession();
    navigate('/bu-dashboard/login', { replace: true });
  }, [clearSession, navigate]);

  useEffect(() => {
    window.addEventListener('attendance-unauthorized', handleUnauthorized);
    return () => window.removeEventListener('attendance-unauthorized', handleUnauthorized);
  }, [handleUnauthorized]);

  useEffect(() => {
    employeeAttendanceAPI.setUnauthorizedHandler(handleUnauthorized);
    employeeAttendanceHrAPI.setUnauthorizedHandler(handleUnauthorized);
    admissionsAPI.setUnauthorizedHandler(handleUnauthorized);
    postgraduateAPI.setUnauthorizedHandler(handleUnauthorized);
    examAPI.setUnauthorizedHandler(handleUnauthorized);
    return () => {
      employeeAttendanceAPI.setUnauthorizedHandler(null);
      employeeAttendanceHrAPI.setUnauthorizedHandler(null);
      admissionsAPI.setUnauthorizedHandler(null);
      postgraduateAPI.setUnauthorizedHandler(null);
      examAPI.setUnauthorizedHandler(null);
    };
  }, [handleUnauthorized]);

  const applyUser = useCallback((userData) => {
    setUser(userData);
    localStorage.setItem(STORAGE_KEY, JSON.stringify(userData));
    const token = localStorage.getItem(TOKEN_KEY) || localStorage.getItem(HR_TOKEN_KEY);
    if (token && (userData?.is_tutor || userData?.is_faculty_admin || userData?.is_camera_media)) {
      window.dispatchEvent(new CustomEvent('attendance-session-sync', {
        detail: { user: userData, token },
      }));
    }
  }, []);

  const buildUser = useCallback(async (staffResult, username, password, officerMeta, options = {}) => {
    const hasStaffPortal = Boolean(options.hasStaffPortal && staffResult?.token);
    const staffToken = hasStaffPortal ? staffResult.token : null;
    if (staffToken) {
      localStorage.setItem(TOKEN_KEY, staffToken);
    }

    let staffProfile = hasStaffPortal ? staffResult || {} : {};
    if (staffToken) {
      try {
        const me = await employeeAttendanceAPI.me(staffToken, { suppressUnauthorized: true });
        if (me?.success) staffProfile = { ...staffResult, ...me };
      } catch {
        /* use login payload */
      }
    }

    const staffFlags = parseOfficerFlags(
      hasStaffPortal ? staffProfile : officerMeta?.officer_profile || {}
    );
    const apiMeta =
      officerMeta ||
      (await resolveOfficerAccess(
        username,
        password,
        staffToken || localStorage.getItem(HR_TOKEN_KEY)
      ));
    const roleMeta = mergeHrAccess(staffFlags, apiMeta);

    const officer = apiMeta.officer_profile || {};
    const emp = (hasStaffPortal ? staffProfile.employee : null) || officer.employee || {};
    const officerEmp = officer.employee || {};
    const officerUser = officer.user || {};

    const pickDataUri = (...candidates) => {
      for (const c of candidates) {
        if (c && String(c).startsWith('data:')) return c;
      }
      return null;
    };
    const image_128 =
      emp.image_128 ||
      officerEmp.image_128 ||
      officerUser.image_128 ||
      officer.image_128 ||
      staffResult?.employee?.image_128 ||
      null;
    const image_url =
      pickDataUri(
        emp.image_url,
        officerEmp.image_url,
        officerUser.image_url,
        officer.image_url,
        staffResult?.employee?.image_url
      ) || (image_128 ? `data:image/jpeg;base64,${image_128}` : null);

    // Shared Bearer: prefer officer (HR login) token for both HR + Admission APIs
    const officerToken = localStorage.getItem(HR_TOKEN_KEY);
    if (officerToken && !localStorage.getItem(TOKEN_KEY)) {
      localStorage.setItem(TOKEN_KEY, officerToken);
    }

    const is_hr_officer =
      staffProfile.is_hr_officer === true ||
      emp.is_hr_officer === true ||
      officer.is_hr_officer === true ||
      roleMeta.is_hr;

    const is_admission_officer =
      staffProfile.is_admission_officer === true ||
      emp.is_admission_officer === true ||
      officer.is_admission_officer === true ||
      roleMeta.is_admission;

    const is_postgraduate_officer =
      staffProfile.is_postgraduate_officer === true ||
      emp.is_postgraduate_officer === true ||
      officer.is_postgraduate_officer === true ||
      roleMeta.is_postgraduate;

    const is_exam_officer =
      staffProfile.is_exam_officer === true ||
      emp.is_exam_officer === true ||
      officer.is_exam_officer === true ||
      roleMeta.is_exam;

    return {
      name: emp.name || officer.name || officerUser.name || staffResult?.employee?.name || officer.employee?.name,
      email: emp.email || officer.email || officerUser.login || staffResult?.employee?.email,
      employee_id: emp.id || officer.employee?.id || staffResult?.employee?.id,
      department: emp.department || officer.department || staffResult?.employee?.department,
      user_id: emp.user_id || officer.employee?.user_id,
      image_128,
      image_url,
      role: staffResult?.role || officer.role || 'employee_attendance',
      expires_at: staffResult?.expires_at || officer.expires_at || null,
      is_hr_officer,
      is_admission_officer,
      is_exam_officer,
      is_postgraduate_officer,
      is_tutor: Boolean(staffProfile.is_tutor || emp.is_tutor),
      is_faculty_admin: Boolean(
        staffProfile.is_faculty_admin
          || emp.is_faculty_admin
          || (staffProfile.roles || []).includes('faculty_admin')
      ),
      faculty: staffProfile.faculty || null,
      faculties: staffProfile.faculties || (staffProfile.faculty ? [staffProfile.faculty] : []),
      roles: staffProfile.roles || [],
      is_camera_media: Boolean(
        staffProfile.is_camera_media
          || (staffProfile.roles || []).includes('camera_media')
      ),
      utilities: staffProfile.utilities || null,
      is_utilities: Boolean(
        staffProfile.utilities
          || (staffProfile.roles || []).includes('utilities')
      ),
      is_staff: hasStaffPortal,
      ...roleMeta,
      is_admission: Boolean(roleMeta.is_admission || is_admission_officer),
      is_exam: Boolean(roleMeta.is_exam || is_exam_officer),
      is_postgraduate: Boolean(roleMeta.is_postgraduate || is_postgraduate_officer),
    };
  }, []);

  useEffect(() => {
    let cancelled = false;

    async function restoreSession() {
      const storedUser = localStorage.getItem(STORAGE_KEY);
      const storedToken =
        localStorage.getItem(TOKEN_KEY) || localStorage.getItem(HR_TOKEN_KEY);

      if (!storedUser || !storedToken) {
        setLoading(false);
        return;
      }

      try {
        const userData = JSON.parse(storedUser);
        if (userData.expires_at) {
          const expiresAt = new Date(userData.expires_at);
          if (expiresAt <= new Date()) {
            clearSession();
            setLoading(false);
            return;
          }
        }

        const officerMeta = await resolveOfficerAccess(null, null, storedToken);
        // Soft restore: keep admission/HR flags from last login when /me endpoints are missing
        const mergedOfficerMeta = {
          ...officerMeta,
          is_hr: Boolean(officerMeta.is_hr || userData.is_hr || userData.is_hr_officer),
          is_admission: Boolean(
            officerMeta.is_admission || userData.is_admission || userData.is_admission_officer
          ),
          is_exam: Boolean(
            officerMeta.is_exam || userData.is_exam || userData.is_exam_officer
          ),
          is_postgraduate: Boolean(
            officerMeta.is_postgraduate || userData.is_postgraduate || userData.is_postgraduate_officer
          ),
          default_dashboard:
            officerMeta.default_dashboard ||
            userData.default_dashboard ||
            (userData.is_exam && !userData.is_hr && !userData.is_admission
              ? 'exam'
              : userData.is_admission && !userData.is_hr
                ? 'admission'
                : null),
          hr_api_error: officerMeta.officer_restore_soft ? null : officerMeta.hr_api_error,
        };
        const hasStaffPortal = Boolean(
          userData.is_staff || localStorage.getItem(TOKEN_KEY)
        );
        const refreshed = await buildUser(
          {
            token: localStorage.getItem(TOKEN_KEY) || storedToken,
            expires_at: userData.expires_at,
            roles: userData.roles || [],
            utilities: userData.utilities || null,
            is_tutor: Boolean(userData.is_tutor),
            is_faculty_admin: Boolean(userData.is_faculty_admin),
            faculty: userData.faculty || null,
            faculties: userData.faculties || (userData.faculty ? [userData.faculty] : []),
            employee: {
              name: userData.name,
              email: userData.email,
              department: userData.department,
              id: userData.employee_id,
              user_id: userData.user_id,
              is_hr_officer: userData.is_hr_officer ?? userData.is_hr,
              is_admission_officer: userData.is_admission_officer ?? userData.is_admission,
              is_exam_officer: userData.is_exam_officer ?? userData.is_exam,
              is_postgraduate_officer: userData.is_postgraduate_officer ?? userData.is_postgraduate,
              image_128: userData.image_128 || null,
              image_url:
                userData.image_url && String(userData.image_url).startsWith('data:')
                  ? userData.image_url
                  : null,
              is_tutor: Boolean(userData.is_tutor),
              is_faculty_admin: Boolean(userData.is_faculty_admin),
            },
          },
          null,
          null,
          mergedOfficerMeta,
          { hasStaffPortal }
        );

        if (!cancelled) applyUser(refreshed);
      } catch (err) {
        console.error('Failed to refresh the saved dashboard session:', err);
        const cachedUser = localStorage.getItem(STORAGE_KEY);
        const cachedToken =
          localStorage.getItem(TOKEN_KEY) || localStorage.getItem(HR_TOKEN_KEY);
        if (!cancelled && cachedUser && cachedToken) {
          try {
            const userData = JSON.parse(cachedUser);
            setUser(userData);
            attendanceAPI.setDefaultFacultyId(
              userData?.faculty?.id || userData?.faculty_id || null
            );
          } catch {
            clearSession();
          }
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    restoreSession();
    return () => {
      cancelled = true;
    };
  }, [clearSession, applyUser, buildUser]);

  const login = async (username, password) => {
    try {
      setLoading(true);
      setError(null);

      // 1) Shared officer login (HR + Admission) — required by admissions handoff
      let officerMeta = null;
      let officerResult = null;
      try {
        officerResult = await employeeAttendanceHrAPI.login(
          username,
          password,
          HR_PROBE_OPTS
        );
        if (officerResult?.success && officerResult.token) {
          localStorage.setItem(HR_TOKEN_KEY, officerResult.token);
          officerMeta = {
            ...parseOfficerFlags(officerResult),
            hr_token_ready: true,
            portal_employee_count: officerResult.portal_employee_count ?? 0,
            officer_profile: officerResult,
          };
        }
      } catch (err) {
        if (err.httpStatus === 404) {
          officerMeta = { is_hr: false, is_admission: false, is_exam: false, is_postgraduate: false, hr_api_missing: true };
        }
      }

      // 2) Staff portal login (attendance Today / History) when available
      let staffResult = null;
      try {
        staffResult = await employeeAttendanceAPI.login(username, password);
      } catch {
        staffResult = null;
      }

      // 3) Existing Tutor / Faculty Admin accounts use the Attendance API.
      // It shares the same attendance.token model, so no credentials or data are copied.
      let attendanceResult = null;
      try {
        attendanceResult = await attendanceAPI.login(username, password);
      } catch {
        attendanceResult = null;
      }
      if (attendanceResult?.success && attendanceResult.token) {
        localStorage.setItem('attendance_token', attendanceResult.token);
        localStorage.setItem(TOKEN_KEY, attendanceResult.token);
      }

      if (
        !officerResult?.token &&
        !(staffResult?.success && staffResult.token) &&
        !(attendanceResult?.success && attendanceResult.token)
      ) {
        const errorMsg =
          officerResult?.message ||
          staffResult?.message ||
          attendanceResult?.message ||
          'Login failed';
        setError(errorMsg);
        return { success: false, error: errorMsg };
      }

      if (!officerMeta && officerResult?.token) {
        officerMeta = await resolveOfficerAccess(username, password, null);
      } else if (!officerMeta && staffResult?.token) {
        officerMeta = await resolveOfficerAccess(username, password, staffResult.token);
      }

      const effectiveStaffResult =
        staffResult?.success && staffResult.token
          ? {
              ...staffResult,
              ...(attendanceResult?.success ? attendanceResult : {}),
              token: attendanceResult?.token || staffResult.token,
              expires_at: attendanceResult?.expires_at || staffResult.expires_at,
              employee: {
                ...(staffResult.employee || {}),
                ...(attendanceResult?.employee || {}),
              },
              is_tutor: Boolean(staffResult.is_tutor || attendanceResult?.is_tutor),
              is_faculty_admin: Boolean(
                staffResult.is_faculty_admin || attendanceResult?.is_faculty_admin
              ),
            }
          : attendanceResult;
      const hasStaffPortal = Boolean(effectiveStaffResult?.success && effectiveStaffResult.token);
      const userData = await buildUser(
        hasStaffPortal
          ? effectiveStaffResult
          : {
              token: officerResult.token,
              expires_at: officerResult.expires_at,
              employee: officerResult.employee,
            },
        username,
        password,
        officerMeta,
        { hasStaffPortal }
      );
      applyUser(userData);
      return { success: true, user: userData, landingPath: getPostLoginPath(userData) };
    } catch (err) {
      const errorMsg = err.message || 'Network error. Please check your connection.';
      setError(errorMsg);
      return { success: false, error: errorMsg };
    } finally {
      setLoading(false);
    }
  };

  const logout = async ({ navigateAway = true } = {}) => {
    try {
      const token = localStorage.getItem(TOKEN_KEY);
      const hrToken = localStorage.getItem(HR_TOKEN_KEY);
      const tasks = [];
      if (token) tasks.push(employeeAttendanceAPI.logout(token).catch(() => {}));
      if (hrToken && hrToken !== token) {
        tasks.push(employeeAttendanceHrAPI.logout(hrToken).catch(() => {}));
      }
      await Promise.all(tasks);
    } catch (err) {
      console.error('Employee attendance logout error:', err);
    } finally {
      clearSession();
      setError(null);
      if (navigateAway) {
        navigate('/bu-dashboard/login');
      }
    }
  };

  const getToken = () => localStorage.getItem(TOKEN_KEY) || localStorage.getItem(HR_TOKEN_KEY);

  const getHrToken = () => localStorage.getItem(HR_TOKEN_KEY) || localStorage.getItem(TOKEN_KEY);

  /** Shared Bearer for HR + Admission APIs */
  const ensureHrToken = useCallback(async () => {
    if (!user?.is_hr && !user?.is_admission && !user?.is_exam && !user?.is_postgraduate) return null;

    // Prefer stored officer token. Do not require /api/admissions/me (often 404).
    // Validate via HR /me when available; otherwise trust unexpired local token.
    const tokenExpired = () => {
      if (!user?.expires_at) return false;
      const t = new Date(user.expires_at);
      return !Number.isNaN(t.getTime()) && t <= new Date();
    };

    if (tokenExpired()) {
      clearSession();
      navigate('/bu-dashboard/login', { replace: true });
      return null;
    }

    let hrToken = localStorage.getItem(HR_TOKEN_KEY) || localStorage.getItem(TOKEN_KEY);
    if (!hrToken) return null;

    // Soft-check /me. Exam/Admission officers often get 403 (not HR) — keep the token.
    try {
      const me = await employeeAttendanceHrAPI.me(hrToken, HR_PROBE_OPTS);
      if (me?.success) {
        localStorage.setItem(HR_TOKEN_KEY, hrToken);
        return hrToken;
      }
    } catch {
      /* use stored shared-login token */
    }

    localStorage.setItem(HR_TOKEN_KEY, hrToken);
    return hrToken;
  }, [user, clearSession, navigate]);

  const isHr = Boolean(user?.is_hr);
  const isAdmission = Boolean(user?.is_admission);
  const isExam = Boolean(user?.is_exam);
  const isPostgraduate = Boolean(user?.is_postgraduate);
  const isUtilities = Boolean(user?.is_utilities);
  const isAuthenticated = !!user && !!getToken();
  const hasBothRoles = isAuthenticated && isHr;
  const hrApiMissing = Boolean(user?.is_hr && user?.hr_api_missing);

  const value = {
    user,
    loading,
    error,
    login,
    logout,
    clearError,
    getToken,
    getHrToken,
    ensureHrToken,
    isHr,
    isAdmission,
    isExam,
    isPostgraduate,
    isUtilities,
    hasBothRoles,
    isAuthenticated,
    hrApiMissing,
    getPostLoginPath: () => getPostLoginPath(user),
  };

  return (
    <EmployeeAttendanceAuthContext.Provider value={value}>
      {children}
    </EmployeeAttendanceAuthContext.Provider>
  );
}

export function useEmployeeAttendanceAuth() {
  const context = useContext(EmployeeAttendanceAuthContext);
  if (!context) {
    throw new Error('useEmployeeAttendanceAuth must be used within EmployeeAttendanceAuthProvider');
  }
  return context;
}

export default EmployeeAttendanceAuthContext;
